import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { RouterOutlet, RouterLink, RouterLinkActive, Router, NavigationEnd } from '@angular/router';
import { CommonModule } from '@angular/common';
import { ThemeService } from '@app/services/common/theme/theme.service';
import { AuthService } from '@app/services/auth/auth/auth.service';
import { filter, Subscription } from 'rxjs';
import { PublicToastComponent } from '@app/common/components/ui/public-toast/public-toast.component';
import { BackToTopComponent } from '@app/common/components/ui/back-to-top/back-to-top';
import { AdminNavItem as NavItem } from '@app/interfaces/admin/view-state.interfaces';

@Component({
  selector: 'app-admin-layout',
  standalone: true,
  imports: [
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    CommonModule,
    PublicToastComponent,
    BackToTopComponent,
  ],
  templateUrl: './admin-layout.component.html',
  styleUrls: ['./admin-layout.component.scss'],
})
export class AdminLayoutComponent implements OnInit, OnDestroy {
  readonly themeService = inject(ThemeService);
  readonly authService = inject(AuthService);
  readonly router = inject(Router);
  private routerSub?: Subscription;

  navItems: NavItem[] = [
    {
      label: 'Home',
      defaultRoute: '/admin/home/content',
      expanded: false,
      children: [
        { label: 'Home Content', route: '/admin/home/content' },
        { label: 'Testimonials', route: '/admin/home/testimonials' },
        { label: 'Gallery', route: '/admin/home/gallery' },
      ],
    },
    {
      label: 'Products',
      defaultRoute: '/admin/products/courses/higher-education',
      expanded: false,
      children: [
        { label: 'Educational Solutions', route: '/admin/products/educational-solutions' },
        { label: 'Industrial Solutions', route: '/admin/products/industrial-solutions' },
        { label: 'Product Catalogs', route: '/admin/products/product-catalogs' },
        {
          label: 'Courses',
          defaultRoute: '/admin/products/courses/higher-education',
          expanded: false,
          children: [
            { label: 'Higher Education', route: '/admin/products/courses/higher-education' },
            { label: 'TVET', route: '/admin/products/courses/tvet' },
            { label: 'Basic Education', route: '/admin/products/courses/basic-education' },
          ],
        },
        {
          label: 'Specialized Products',
          defaultRoute: '/admin/products/specialized-products/product-categories',
          expanded: false,
          children: [
            {
              label: 'Product Categories',
              route: '/admin/products/specialized-products/product-categories',
            },
            {
              label: 'Products and Courses',
              route: '/admin/products/specialized-products/products-and-courses',
            },
          ],
        },
      ],
    },
    {
      label: 'Partners',
      defaultRoute: '/admin/partners/academe',
      expanded: false,
      children: [
        { label: 'Academe Partners', route: '/admin/partners/academe' },
        { label: 'Industrial Partners', route: '/admin/partners/industrial' },
      ],
    },
    { label: 'News & Updates', route: '/admin/news-and-updates' },
    { label: 'Trainings & Events', route: '/admin/trainings-and-events' },
    { label: 'Virtual Tour', route: '/admin/virtual-tour' },
    {
      label: 'Company',
      defaultRoute: '/admin/company/about-us/our-journey',
      expanded: false,
      children: [
        {
          label: 'About Us',
          defaultRoute: '/admin/company/about-us/our-journey',
          expanded: false,
          children: [
            { label: 'Our Journey', route: '/admin/company/about-us/our-journey' },
            { label: 'Awards & Achievements', route: '/admin/company/about-us/awards-and-achievements' },
            { label: 'Programs', route: '/admin/company/about-us/programs' },
            { label: 'President and CEO', route: '/admin/company/about-us/president-and-ceo' },
            { label: 'Mission and Vision', route: '/admin/company/about-us/mission-and-vision' },
          ],
        },
        { label: 'Sponsorships', route: '/admin/company/sponsorships' },
        { label: 'Team', route: '/admin/company/team' },
        { label: 'Our Contacts', route: '/admin/company/contacts' },
      ],
    },
    { label: 'Hirings', route: '/admin/hirings' },
  ];

  ngOnInit(): void {
    // Expand ancestors for the current route on first load.
    this.syncExpandedState(this.router.url);

    // Re-sync whenever navigation completes.
    this.routerSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.syncExpandedState(e.urlAfterRedirects));
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  toggleGroup(item: NavItem): void {
    item.expanded = !item.expanded;
  }

  logout(): void {
    this.authService.logout().subscribe({ error: () => undefined });
    this.authService.clearSession();
    void this.router.navigate(['/']);
  }

  /** Returns true if any descendant of item matches the url path. */
  hasActiveDescendant(item: NavItem, url: string): boolean {
    if (!item.children) return false;
    return item.children.some(
      (c) =>
        (c.route && url.startsWith(c.route)) ||
        (c.defaultRoute && url.startsWith(c.defaultRoute)) ||
        this.hasActiveDescendant(c, url),
    );
  }

  /** Recursively expands groups that contain the active route. */
  private syncExpandedState(url: string): void {
    for (const item of this.navItems) {
      if (item.children) {
        if (this.hasActiveDescendant(item, url)) {
          item.expanded = true;
          // Also expand nested groups (e.g. About Us inside Company)
          for (const child of item.children) {
            if (child.children && this.hasActiveDescendant(child, url)) {
              child.expanded = true;
            }
          }
        }
      }
    }
  }
}
